import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';

// Local type definition - ConfirmDialogData is not exported from core-lib
interface ConfirmDialogData {
  title?: string;
  subTitle?: string;
  text?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButton?: boolean;
  cancelButton?: boolean;
  disableClose?: boolean;
}

interface CsvImportValidationError {
  code?: string;
  rowNumber?: number;
  field?: string;
  parameters?: Record<string, string>;
}

import { NotificationActions } from '@next/core-lib/notification';
import { SiteActions } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  endWith,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getDiscountSettings,
  getFinalSetupSettings,
} from 'src/app/settings/store/selectors/setting.selector';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import * as feedAdviceActions from '../../../feed-advice/store/feed-advice.actions';
import * as productActions from '../../../product/store/actions/product.actions';
import * as referenceSchemeActions from '../../../reference-scheme/store/reference-scheme.actions';
import * as refValSheetActions from '../../../reference-value-sheet/store/reference-value-sheet.actions';
import * as settingActions from '../../../settings/store/actions/setting.actions';
import { PoultryContextKeys } from '../../../shared/constants';
import {
  CarbonFootprintResponse,
  Concept,
  Cycle,
  CycleModuleState,
  Delivery,
  Product,
} from '../../models/cycle.model';
import {
  GetFarmResourcesResponse,
  SlaughterInvoicesResponse,
  SlaughterReportsResponse,
  UpsertCycleResponse,
  UpsertFarmResourceResponse,
} from '../../models/flock-registration-service.model';
import { Hatchery } from '../../models/hatchery-service.model';
import { Slaughterhouse } from '../../models/slaughterhouse-service.model';
import { ConceptService } from '../../services/concept.service';
import { DiscountService } from '../../services/discount.service';
import { FlockRegistrationService } from '../../services/flock-registration.service';
import { HatcheryService } from '../../services/hatchery.service';
import { SlaughterhouseService } from '../../services/slaughterhouse.service';
import * as cycleActions from '../actions/cycle.actions';
import { getSelectedCycle, getSelectedCycleId } from '../selectors/cycle.selectors';

@Injectable()
export class CycleEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<CycleModuleState>>(Store);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private flockRegistrationService = inject(FlockRegistrationService);
  private hatcheryService = inject(HatcheryService);
  private slaughterhouseService = inject(SlaughterhouseService);
  private conceptService = inject(ConceptService);
  private dialogService = inject(DialogService);
  private readonly locale = inject<LocaleService>(LocaleService);
  private settingsHelper = inject(SettingsServiceHelper);
  private discountService = inject(DiscountService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadData),
      switchMap(() => [
        cycleActions.clearCycleState(),
        productActions.loadSelectedCountryRequest(),
        refValSheetActions.loadSelectedCountryRequest(),
        referenceSchemeActions.loadSelectedCountryRequest(),
        settingActions.loadObservationSwitch(),
        settingActions.loadBarnsRequest(),
        settingActions.loadDayOffset(),
        settingActions.loadUnitOfMeasureSettings(),
        settingActions.loadVeterinarianSettings(),
        settingActions.loadFinalSetupSettings(),
        settingActions.loadDiscountSettings(),
        cycleActions.loadConceptsRequest(),
        cycleActions.loadHatcheriesRequest(),
        cycleActions.loadSlaughterhousesRequest(),
        cycleActions.loadCyclesRequest(),
        refValSheetActions.loadRefValSheetsRequest(),
        feedAdviceActions.loadFeedAdvicesRequest(),
      ]),
    ),
  );

  loadCycles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadCyclesRequest),
      mergeMap(() =>
        this.flockRegistrationService.getCycles().pipe(
          map((cycles: Cycle[] | HttpErrorResponse) =>
            (cycles as Cycle[]).sort((c1: Cycle, c2: Cycle) => c2.cycleNumber - c1.cycleNumber),
          ),
          withLatestFrom(this.store.select(getSelectedCycleId)),
          switchMap(([cycles]) => of(cycleActions.loadCyclesSuccess({ cycles }))),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadCyclesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCycleRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteCycleRequest),
      mergeMap(({ cycle }) =>
        this.flockRegistrationService.deleteCycle(cycle.id).pipe(
          map(() => cycleActions.deleteCycleSuccess({ cycle })),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.deleteCycleFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCycleSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cycleActions.deleteCycleSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/cycle'])).then();
        }),
      ),
    { dispatch: false },
  );

  loadConcepts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadConceptsRequest),
      mergeMap(() =>
        this.conceptService.getConcepts().pipe(
          map((concepts: Concept[]) => cycleActions.loadConceptsSuccess({ concepts })),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadConceptsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadHatcheries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadHatcheriesRequest),
      mergeMap(() =>
        this.hatcheryService.getHatcheries().pipe(
          map((hatcheries: Hatchery[]) => cycleActions.loadHatcheriesSuccess({ hatcheries })),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadHatcheriesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSlaughterhouses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadSlaughterhousesRequest),
      mergeMap(() =>
        this.slaughterhouseService.getSlaughterhouses().pipe(
          map((slaughterhouses: Slaughterhouse[]) =>
            cycleActions.loadSlaughterhousesSuccess({ slaughterhouses }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadSlaughterhousesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadProductsRequest),
      mergeMap(({ cycleId, previousCycleCount }) =>
        this.flockRegistrationService.getProductsToConsume(cycleId, previousCycleCount).pipe(
          map((products: Product[]) => cycleActions.loadProductsSuccess({ products })),
          catchError((error: HttpErrorResponse) => of(cycleActions.loadProductsFailed({ error }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  createCycle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.createCycle),
      map(({ cycle }) => cycleActions.upsertCycleRequest({ cycle })),
    ),
  );

  saveSelectedCycle$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        cycleActions.upsertCycleGeneral,
        cycleActions.addDayOldChick,
        cycleActions.editDayOldChick,
        cycleActions.deleteDayOldChick,
        cycleActions.addDeparture,
        cycleActions.editDeparture,
        cycleActions.deleteDeparture,
        cycleActions.addBalanceRow,
        cycleActions.deleteBalanceRow,
        cycleActions.upsertCosts,
        cycleActions.addDiscount,
        cycleActions.addDiscounts,
        cycleActions.editDiscount,
        cycleActions.deleteDiscount,
        cycleActions.upsertAnimalGroup,
      ),
      switchMap((_) => this.store.pipe(select(getSelectedCycle), take(1))),
      map((cycle: Cycle) => cycleActions.upsertCycleRequest({ cycle })),
    ),
  );

  upsertCycleRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.upsertCycleRequest),
      exhaustMap(({ cycle }) =>
        this.flockRegistrationService.upsertCycle(cycle).pipe(
          switchMap((response: UpsertCycleResponse) => {
            const actions: Action[] = [
              cycleActions.upsertCycleSuccess({
                cycle: {
                  ...cycle,
                  id: response.id,
                },
              }),
            ];
            if (cycle.cycleDate) {
              actions.push(
                cycleActions.loadSlaughterReportsRequest({
                  fromDate: '',
                }),
              );
            }
            return actions;
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.upsertCycleFailed({ error }),
              cycleActions.loadCyclesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertBalancesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.upsertBalancesRequest),
      exhaustMap(({ upsertBalances }) =>
        this.flockRegistrationService.upsertBalances(upsertBalances).pipe(
          map((_: UpsertCycleResponse) =>
            cycleActions.upsertBalancesSuccess({
              upsertBalances,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.upsertBalancesFailed({ error }),
              cycleActions.loadCyclesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  calculateBalanceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.calculateBalanceRequest),
      switchMap((_) =>
        this.store.select(getSelectedCycle).pipe(
          skipWhile((c) => !c?.id),
          take(1),
        ),
      ),
      mergeMap((cycle) =>
        this.flockRegistrationService.calculateBalanceDraft(cycle.id).pipe(
          map((responseCycle: Cycle | HttpErrorResponse) =>
            cycleActions.calculateBalanceSuccess({ cycle: responseCycle as Cycle }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(cycleActions.calculateBalanceError({ error })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  importManualDeliveriesCsvRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.importManualDeliveriesCsvRequest),
      exhaustMap(({ cycleId, file }) =>
        this.flockRegistrationService.importManualDeliveriesCsv(cycleId, file).pipe(
          switchMap(() =>
            of(
              cycleActions.importManualDeliveriesCsvSuccess(),
              NotificationActions.successSnackbar({
                message: this.locale.translate('csv-import.delivery-success'),
              }),
              cycleActions.calculateBalanceRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.importManualDeliveriesCsvFailed({ error }),
              NotificationActions.errorSnackbar({
                message: this.getCsvImportErrorMessage(error),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  importDeparturesCsvRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.importDeparturesCsvRequest),
      exhaustMap(({ cycleId, file }) =>
        this.flockRegistrationService.importDeparturesCsv(cycleId, file).pipe(
          switchMap(() =>
            of(
              cycleActions.importDeparturesCsvSuccess(),
              NotificationActions.successSnackbar({
                message: this.locale.translate('csv-import.departure-success'),
              }),
              cycleActions.loadCyclesRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.importDeparturesCsvFailed({ error }),
              NotificationActions.errorSnackbar({
                message: this.getCsvImportErrorMessage(error),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  calculateBalanceSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.calculateBalanceSuccess),
      concatMap(() => of(cycleActions.setBalancePendingChanges())),
    ),
  );

  setBalancePendingChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.setBalancePendingChanges),
      concatMap(() =>
        of(
          SiteActions.setPendingChanges({
            pendingChange: {
              id: 'balance-changes',
              title: this.locale.translate(`balance.pending-changes-warning.title`),
              message: this.locale.translate(`balance.pending-changes-warning.message`),
              confirmText: this.locale.translate(`balance.pending-changes-warning.confirmText`),
              cancelText: this.locale.translate(`balance.pending-changes-warning.cancelText`),
            },
          }),
        ),
      ),
    ),
  );

  upsertCycleSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cycleActions.upsertCycleSuccess),
        map((action) => action.cycle),
        tap((cycle) => {
          this.store.dispatch(SiteActions.setNoPendingChanges({ ids: ['balance-changes'] }));
          this.store.dispatch(
            cycleActions.setRouteTransitionBalance({ routeTransitionBalance: null }),
          );
          const urlParts = this.router.url.split('?');
          const url = urlParts.length > 0 ? urlParts[0] : this.router.url;
          if (url.indexOf('day-old-chick') > 0 || url.indexOf('/cycle/add') >= 0) {
            this.store.dispatch(cycleActions.loadCyclesRequest());
            this.ngZone
              .run(() => this.router.navigate(['/cycle', cycle.id, `day-old-chick`]))
              .then();
          } else if (url.indexOf('departure') >= 0) {
            this.ngZone.run(() => this.router.navigate(['/cycle', cycle.id, `departure`])).then();
          } else if (url.indexOf('balance') >= 0) {
            this.ngZone.run(() => this.router.navigate(['/cycle', cycle.id, `balance`])).then();
          } else if (url.indexOf('discount') >= 0) {
            this.ngZone.run(() => this.router.navigate(['/cycle', cycle.id, `discount`])).then();
          } else if (url.indexOf('final-setup') >= 0) {
            this.ngZone.run(() => this.router.navigate(['/cycle', cycle.id, `final-setup`])).then();
          } else if (url.indexOf('animal-group') >= 0) {
            this.ngZone
              .run(() => this.router.navigate(['/cycle', cycle.id, `animal-group`]))
              .then();
          } else if (url.indexOf('/cycle') >= 0) {
            this.ngZone.run(() => this.router.navigate(['/cycle'])).then();
          } else {
            this.store.dispatch(cycleActions.loadCyclesRequest());
          }
        }),
      ),
    { dispatch: false },
  );

  upsertBalancesSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.upsertBalancesSuccess),
      switchMap((action) =>
        of(
          SiteActions.setNoPendingChanges({ ids: ['balance-changes'] }),
          cycleActions.setRouteTransitionBalance({ routeTransitionBalance: null }),
          cycleActions.loadCyclesRequest(),
          cycleActions.addAutomaticDiscounts({
            isFinalBalance: action.upsertBalances.isFinalBalance,
          }),
        ),
      ),
    ),
  );

  deleteDayOldChickRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteDayOldChickWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning(
            action.isUsedInBalance
              ? {
                  text: this.locale.translate('delete-item-day-old-chick-used-in-balance'),
                }
              : this.getDeleteDialogConfirmation('day-old-chick'),
          )
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  cycleActions.deleteDayOldChick({ cycleId: action.cycleId, id: action.id }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteDepartureWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteDepartureWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfirmation('departure')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteDeparture({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteBalanceRowWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteBalanceRowWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfirmation('balance')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteBalanceRow({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteCycleWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteCycleWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfirmation('flock-cycle')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteCycleRequest({
                  cycle: action.cycle,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteDiscountWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteDiscountWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfirmation('discount')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                cycleActions.deleteDiscount({
                  cycleId: action.cycleId,
                  id: action.id,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  loadIsCycleDetailedOverview$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.browserInitialized),
      switchMap((_) =>
        this.settingsHelper
          .getSingle<boolean>(false, PoultryContextKeys.CYCLES_IS_DETAILED_OVERVIEW)
          .pipe(
            switchMap((isDetailedOverview) =>
              of(
                cycleActions.setIsCycleDetailedOverview({
                  value: isDetailedOverview,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  setIsCycleDetailedOverview$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cycleActions.setIsCycleDetailedOverview),
        tap((action) =>
          this.settingsHelper.setSettings(
            PoultryContextKeys.CYCLES_IS_DETAILED_OVERVIEW,
            action.value,
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  setSelectedCycle$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cycleActions.setSelectedCycle),
        tap((action) =>
          this.settingsHelper.setSettings(
            PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID,
            action.cycleId,
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  loadDeliveries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadDeliveriesRequest),
      exhaustMap((_) =>
        this.flockRegistrationService.getDeliveries().pipe(
          switchMap((deliveries: Delivery[]) =>
            of(cycleActions.loadDeliveriesSuccess({ deliveries })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadDeliveriesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSlaughterReports$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadSlaughterReportsRequest),
      exhaustMap((request) =>
        this.flockRegistrationService.getSlaughterReports(request.fromDate).pipe(
          switchMap((response: SlaughterReportsResponse) =>
            of(
              cycleActions.loadSlaughterReportsSuccess({
                slaughterReports: response.slaughterReports,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadSlaughterReportsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  linkSlaughterReports$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.linkSlaughterReportsRequest),
      exhaustMap((request) =>
        this.flockRegistrationService
          .importFromSlaughterReports(request.cycleId, request.links)
          .pipe(
            switchMap((_) =>
              of(
                cycleActions.loadData(),
                cycleActions.loadSlaughterReportsRequest({
                  fromDate: '',
                }),
              ),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                cycleActions.linkSlaughterReportsFailed({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  loadSlaughterInvoicesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadSlaughterInvoicesRequest),
      concatMap((request) =>
        this.flockRegistrationService
          .getSlaughterInvoices(request.fromDate, request.linkedCycleId)
          .pipe(
            map((response: SlaughterInvoicesResponse) =>
              cycleActions.loadSlaughterInvoicesSuccess({
                slaughterInvoices: response.slaughterInvoices,
                isLinked: !!request.linkedCycleId,
              }),
            ),
            catchError((error: HttpErrorResponse) => [
              NotificationActions.errorSnackbar({ message: error.message }),
            ]),
            startWith(CoreActions.setLoading({ loading: true })),
            endWith(CoreActions.setLoading({ loading: false })),
          ),
      ),
    ),
  );

  linkSlaughterInvoices$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.linkSlaughterInvoices),
      concatMap((request) =>
        this.flockRegistrationService.linkSlaughterInvoices(request.cycleId, request.ids).pipe(
          map(() => cycleActions.linkSlaughterInvoicesSuccess()),
          catchError((error: HttpErrorResponse) => [
            NotificationActions.errorSnackbar({ message: error.message }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          endWith(CoreActions.setLoading({ loading: false })),
        ),
      ),
    ),
  );

  addAutomaticDiscounts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.addAutomaticDiscounts),
      switchMap((action) =>
        this.actions$.pipe(
          ofType(cycleActions.loadCyclesSuccess),
          map((_) => action),
          take(1),
        ),
      ),
      withLatestFrom(
        this.store.select(getSelectedCycle),
        this.store.select(getFinalSetupSettings),
        this.store.select(UserSelectors.selectLocation),
        this.store.select(getDiscountSettings),
      ),
      map(([action, cycle, finalSetupSettings, selectedLocation, discountSettings]) => {
        const discounts = this.discountService.calculateAutomaticDiscountsForCycle(
          action.isFinalBalance,
          cycle,
          finalSetupSettings,
          selectedLocation,
          discountSettings,
        );

        if (discounts.length > 0) {
          return cycleActions.addDiscounts({ cycleId: cycle.id, discounts });
        }

        return cycleActions.addAutomaticDiscountsSkipped();
      }),
    ),
  );

  getDeleteDialogConfirmation(type: string): ConfirmDialogData {
    return {
      text: this.locale.translate(`delete-item-${type}`),
    };
  }

  private getCsvImportErrorMessage(error: HttpErrorResponse): string {
    const csvErrors = error.error?.csvErrors;
    const importErrors = error.error?.errors;

    if (Array.isArray(csvErrors) && csvErrors.length > 0) {
      return csvErrors
        .map((csvError: CsvImportValidationError, index: number) =>
          this.translateCsvImportError(
            csvError,
            Array.isArray(importErrors) ? importErrors[index] : undefined,
          ),
        )
        .join('\n');
    }

    if (Array.isArray(importErrors) && importErrors.length > 0) {
      return importErrors.join('\n');
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    return error.error?.message ?? error.message;
  }

  private translateCsvImportError(csvError: CsvImportValidationError, fallback?: string): string {
    const code = csvError.code;
    if (!code) {
      return fallback ?? 'csv-import.errors.unknown';
    }

    const key = `csv-import.errors.${code}`;
    const rowNumber = csvError.rowNumber;
    const field = csvError.field;
    const parameters = csvError.parameters ?? {};
    const replacements: Record<string, string> = { ...parameters };

    if (rowNumber !== undefined && rowNumber !== null) {
      replacements.rowNumber = rowNumber.toString();
    }

    if (field) {
      replacements.field = field;
    }

    const translated = this.locale.translate(key, replacements);
    return translated === key ? (fallback ?? key) : translated;
  }

  loadFarmResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadFarmResourcesRequest),
      switchMap(() =>
        this.flockRegistrationService.getFarmResources().pipe(
          map((response: GetFarmResourcesResponse) =>
            cycleActions.loadFarmResourcesSuccess({
              farmResources: response.farmResources,
              cycleResources: response.cycleResources,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadFarmResourcesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFarmResource$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.upsertFarmResourceRequest),
      mergeMap((request) =>
        this.flockRegistrationService.upsertFarmResource(request.request).pipe(
          switchMap((response: UpsertFarmResourceResponse) =>
            of(
              cycleActions.upsertFarmResourceSuccess({
                farmResource: { ...request.request, id: response.id },
              }),
              NotificationActions.successSnackbar({
                message: this.locale.translate('sustainability.farm-resource-saved'),
              }),
              cycleActions.loadFarmResourcesRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.upsertFarmResourceError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteFarmResourceWithConfirmationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteFarmResourceWithConfirmationRequest),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: this.locale.translate('delete'),
            text: this.locale.translate('sustainability.confirm-delete-farm-resource'),
          })
          .pipe(
            switchMap((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                return [cycleActions.deleteFarmResourceRequest({ id: action.id })];
              }
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  deleteFarmResourceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.deleteFarmResourceRequest),
      mergeMap(({ id }) =>
        this.flockRegistrationService.deleteFarmResource(id).pipe(
          switchMap(() =>
            of(
              cycleActions.deleteFarmResourceSuccess({ id }),
              NotificationActions.successSnackbar({
                message: this.locale.translate('sustainability.farm-resource-deleted'),
              }),
              cycleActions.loadFarmResourcesRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.deleteFarmResourceError({ error }),
              NotificationActions.successSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadCarbonFootprintResult$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.loadCarbonFootprintRequest),
      switchMap(({ cycleId }) =>
        this.flockRegistrationService.getCarbonFootprint(cycleId).pipe(
          switchMap((data: CarbonFootprintResponse) =>
            of(cycleActions.loadCarbonFootprintSuccess({ data })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              cycleActions.loadCarbonFootprintError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  calculateCarbonFootprintResult$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.calculateCarbonFootprintRequest),
      mergeMap(({ cycleId }) => {
        return this.flockRegistrationService.calculateCarbonFootprint(cycleId).pipe(
          // Wait for a certain amount of time for the asynchronous request to finish.
          // It could be replaced by a polling mechanism in the future, based on the Status returned by the Sustell API
          delay(70000),
          map(() => cycleActions.loadCarbonFootprintRequest({ cycleId })),
          catchError((error: HttpErrorResponse) => {
            const errors = error.error?.errorMessages ?? error.message;
            const formattedErrors = Array.isArray(errors) ? errors.join('\n') : errors;
            const errorMessages =
              error.error?.readiness?.canCalculate === false
                ? this.locale.translate('sustainability.readiness.blocked')
                : formattedErrors;

            this.dialogService.confirmWarning({
              title: this.locale.translate('sustainability.carbon-footprint-title-error'),
              text: errorMessages,
            });

            return of(
              cycleActions.calculateCarbonFootprintFailure({ error: errorMessages }),
              cycleActions.loadCarbonFootprintRequest({ cycleId }),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  refreshCarbonFootprintResult$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cycleActions.refreshCarbonFootprintRequest),
      mergeMap(({ cycleId }) =>
        this.flockRegistrationService.refreshCarbonFootprint(cycleId).pipe(
          map(() => cycleActions.loadCarbonFootprintRequest({ cycleId })),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = error.error?.errorMessage ?? error.message;

            this.dialogService.confirmWarning({
              title: this.locale.translate('sustainability.carbon-footprint-refresh-error-title'),
              text: this.locale.translate('sustainability.carbon-footprint-refresh-error-text'),
            });

            return of(
              cycleActions.refreshCarbonFootprintFailure({ error: new Error(errorMessage) }),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
